/**
 * Deep links across the login round trip (#92).
 *
 * Hash fragments don't reliably survive the redirect out to TYPO3/Ory and
 * back, so the intended module path is parked in sessionStorage. It holds
 * a module path such as '/account/security' and nothing else — never
 * document identity (dec_f28a22e186, dec_02efdeb85e): a query such as
 * '?document=<id>' is dropped before saving.
 */

import { logger } from '../../core/logger';
import { STORAGE_KEYS } from '../../core/app-constants';
import { parseHash, type Router } from './router';

export const INTENDED_ROUTE_KEY = STORAGE_KEYS.INTENDED_ROUTE;

/** The accessor itself throws in some private-browsing configurations. */
function sessionStorageOrNull(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function saveIntendedRoute(
  hash: string,
  storage: Storage | null = sessionStorageOrNull()
): void {
  const path = parseHash(hash)?.split('?')[0];
  if (!path || path === '/') return;
  try {
    storage?.setItem(INTENDED_ROUTE_KEY, path);
  } catch (error) {
    logger.warn('Could not save intended route:', error);
  }
}

export function consumeIntendedRoute(
  storage: Storage | null = sessionStorageOrNull()
): string | null {
  try {
    const value = storage?.getItem(INTENDED_ROUTE_KEY) ?? null;
    storage?.removeItem(INTENDED_ROUTE_KEY);
    return value !== null && !value.includes('?') && parseHash(value) === value
      ? value
      : null;
  } catch (error) {
    logger.warn('Could not read intended route:', error);
    return null;
  }
}

/** Send the user where they meant to go — unless they arrived deep-linked. */
export function applyIntendedRoute(
  router: Pick<Router, 'current' | 'replace'>,
  intended: string | null
): void {
  if (intended && router.current() === '/') router.replace(intended);
}
